/**
 * /admin/diag/stats — Dashboard estadístico de errores frontend.
 *
 * - Selector de rango: 7d / 14d / 30d / 90d
 * - KPIs: total reports, unique errors, users affected
 * - Timeline diaria (gráfico de barras)
 * - Top errors, top URLs, top users
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { AlertTriangle, BarChart3, Users, ExternalLink, ArrowLeft, RefreshCw, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const RANGOS = [
  { v: 7, label: "7 días" },
  { v: 14, label: "14 días" },
  { v: 30, label: "30 días" },
  { v: 90, label: "90 días" },
];

export default function AdminDiagStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/diag/frontend-errors/stats", { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  // Calcular max de la timeline para escalar las barras
  const maxDay = useMemo(() => {
    if (!data?.timeline_daily) return 1;
    return Math.max(1, ...data.timeline_daily.map((d) => d.count));
  }, [data]);

  return (
    <div className="p-6 max-w-[1800px]" data-testid="admin-diag-stats-page">
      <PageHeader
        title="Diagnóstico · Estadísticas"
        subtitle="Tendencias y patrones de errores frontend. Detecta qué módulos crashean más y qué usuarios están más afectados."
        action={
          <div className="flex items-center gap-2">
            <Link to="/admin/diag">
              <Button variant="outline" data-testid="back-to-diag-list" className="bg-transparent border-zinc-700 text-zinc-300">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Ver lista
              </Button>
            </Link>
            <Button variant="outline" onClick={load} data-testid="stats-refresh" className="bg-transparent border-zinc-700 text-zinc-300">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </Button>
          </div>
        }
      />

      {/* Rango */}
      <div className="flex items-center gap-2 mt-4 mb-6">
        <span className="text-xs text-zinc-500 mr-2">Rango:</span>
        {RANGOS.map((r) => (
          <Button
            key={r.v}
            variant={days === r.v ? "default" : "outline"}
            size="sm"
            onClick={() => setDays(r.v)}
            data-testid={`range-${r.v}d`}
            className={days === r.v
              ? "bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
              : "bg-transparent border-zinc-800 text-zinc-400"}>
            {r.label}
          </Button>
        ))}
      </div>

      {!data ? (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center text-zinc-500">
          {loading ? "Cargando…" : "Sin datos"}
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <Kpi label="Reportes totales" value={data.total_reports} icon={AlertTriangle} color="amber" testId="kpi-total" />
            <Kpi label="Errores únicos" value={data.unique_errors} icon={BarChart3} color="blue" testId="kpi-unique" />
            <Kpi label="Usuarios afectados" value={data.users_affected} icon={Users} color="emerald" testId="kpi-users" />
            <Kpi label="URLs con crashes" value={data.top_urls.length} icon={ExternalLink} color="violet" testId="kpi-urls" />
          </div>

          {/* Timeline */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-zinc-200">Tendencia · últimos {days} días</h3>
              <span className="ml-auto text-[10px] text-zinc-500">máx {maxDay}/día</span>
            </div>
            <div className="flex items-end gap-1 h-36" data-testid="timeline-chart">
              {data.timeline_daily.map((d) => {
                const pct = Math.round((d.count / maxDay) * 100);
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${d.date}: ${d.count}`}>
                    <div className="text-[10px] font-mono text-zinc-400 mb-1 h-3">
                      <span className={d.count > 0 ? "opacity-0 group-hover:opacity-100 transition-opacity" : "opacity-0"}>
                        {d.count}
                      </span>
                    </div>
                    <div className="w-full flex items-end" style={{ height: "calc(100% - 36px)" }}>
                      <div
                        className={`w-full rounded-t ${d.count > 0 ? "bg-amber-500/60 group-hover:bg-amber-400" : "bg-zinc-800"} transition-colors`}
                        style={{ height: `${Math.max(3, pct)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-2 font-mono whitespace-nowrap">
                      {d.date.slice(5)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top errors + Top URLs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <RankList
              title="Top errores recurrentes"
              icon={AlertTriangle}
              items={data.top_errors}
              testId="top-errors"
              renderItem={(e, i) => (
                <div className="flex items-center gap-3 p-3 border-b border-zinc-800/40 hover:bg-zinc-900/40">
                  <span className="text-xs font-mono text-zinc-500 w-6">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-zinc-200 truncate" title={e.message}>{e.message}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {e.users_affected} usuario{e.users_affected === 1 ? "" : "s"} · {e.urls_affected} URL{e.urls_affected === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-amber-400 px-2 py-0.5 bg-amber-500/15 rounded font-mono">×{e.count}</span>
                </div>
              )}
            />
            <RankList
              title="URLs problemáticas"
              icon={ExternalLink}
              items={data.top_urls}
              testId="top-urls"
              renderItem={(u, i) => (
                <div className="flex items-center gap-3 p-3 border-b border-zinc-800/40 hover:bg-zinc-900/40">
                  <span className="text-xs font-mono text-zinc-500 w-6">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-zinc-200 truncate" title={u.url}>{u.url.replace(/^https?:\/\/[^/]+/, "")}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {u.users_affected} usuario{u.users_affected === 1 ? "" : "s"} afectado{u.users_affected === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-violet-400 px-2 py-0.5 bg-violet-500/15 rounded font-mono">×{u.count}</span>
                </div>
              )}
            />
          </div>

          {/* Top users */}
          <RankList
            title="Usuarios más afectados"
            icon={Users}
            items={data.top_users}
            testId="top-users"
            renderItem={(u, i) => (
              <div className="flex items-center gap-3 p-3 border-b border-zinc-800/40 hover:bg-zinc-900/40">
                <span className="text-xs font-mono text-zinc-500 w-6">{i + 1}.</span>
                <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-xs font-bold text-emerald-400 shrink-0">
                  {(u.user || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-200 truncate">{u.user}</div>
                </div>
                <span className="text-sm font-bold text-emerald-400 px-2 py-0.5 bg-emerald-500/15 rounded font-mono">×{u.count}</span>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testId }) {
  const colorMap = {
    amber:   "from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400",
    blue:    "from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400",
    violet:  "from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-400",
  };
  return (
    <div data-testid={testId} className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80 mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-bold text-zinc-100 font-mono">{value}</div>
    </div>
  );
}

function RankList({ title, icon: Icon, items, renderItem, testId }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
        <Icon className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
        <span className="ml-auto text-xs text-zinc-500">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-zinc-500">Sin datos</div>
      ) : (
        <div>{items.map((it, i) => <div key={i}>{renderItem(it, i)}</div>)}</div>
      )}
    </div>
  );
}
